// src/modules/ida/hooks/useHeartbeats.ts
//
// What? React hook for listing + mutating recurring heartbeats.
// Why? Sticky panel needs workspace-visible list rows; editor needs
//      save and cancel. Backend proxy routes live at
//      /api/workspace/{workspace_id}/scheduler/tasks.
// How? Plain useState + useEffect (no react-query dep in this codebase today).
//      Exposes a refresh() for the UI to call after mutations.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  heartbeatApi,
  HeartbeatEditPayload,
  HeartbeatEditResponse,
  HeartbeatSummary,
} from "../services/heartbeats";
import { addHeartbeatsChangedListener } from "../utils/heartbeatEvents";

interface UseHeartbeatsOptions {
  workspaceId: string | null;
  // Optional specialized filter. The main workspace panel intentionally
  // omits this so active heartbeats stay visible across chats.
  conversationId?: number | null;
  // Skip the fetch entirely (e.g. for non-admin users). Panel still
  // mounts but resolves to empty/inactive -- caller hides the UI.
  enabled?: boolean;
}

interface UseHeartbeatsResult {
  items: HeartbeatSummary[];
  schedulerAvailable: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  cancel: (taskId: string) => Promise<void>;
  edit: (
    taskId: string,
    payload: HeartbeatEditPayload,
  ) => Promise<HeartbeatEditResponse>;
}

export function useHeartbeats({
  workspaceId,
  conversationId,
  enabled = true,
}: UseHeartbeatsOptions): UseHeartbeatsResult {
  const authFetch = useAuthFetch("json");
  const [items, setItems] = useState<HeartbeatSummary[]>([]);
  const [schedulerAvailable, setSchedulerAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep authFetch stable across renders for the callback deps closure --
  // useAuthFetch returns a new fn each render by design.
  const authFetchRef = useRef(authFetch);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshQueuedRef = useRef(false);
  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const performRefresh = useCallback(async () => {
    if (!enabled || !workspaceId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await heartbeatApi.list(
        authFetchRef.current,
        workspaceId,
        conversationId ?? null,
      );
      setItems(res.items);
      setSchedulerAvailable(res.scheduler_available);
    } catch (err) {
      // Admin-gate 403 is expected on non-admin calls; just hide the panel.
      if ((err as { status?: number }).status === 403) {
        setItems([]);
        setSchedulerAvailable(false);
      } else {
        setError((err as Error).message || "Failed to load heartbeats");
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, workspaceId, conversationId]);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      await refreshInFlightRef.current;
      return;
    }

    const refreshPromise = (async () => {
      do {
        refreshQueuedRef.current = false;
        await performRefresh();
      } while (refreshQueuedRef.current);
    })().finally(() => {
      refreshInFlightRef.current = null;
    });

    refreshInFlightRef.current = refreshPromise;
    await refreshPromise;
  }, [performRefresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || !workspaceId) return undefined;
    return addHeartbeatsChangedListener(() => {
      void refresh();
    });
  }, [enabled, workspaceId, refresh]);

  const cancel = useCallback(
    async (taskId: string) => {
      if (!workspaceId) return;
      await heartbeatApi.cancel(authFetchRef.current, workspaceId, taskId);
      await refresh();
    },
    [workspaceId, refresh],
  );

  const edit = useCallback(
    async (taskId: string, payload: HeartbeatEditPayload) => {
      if (!workspaceId) {
        throw new Error("workspace_id missing");
      }
      const res = await heartbeatApi.edit(
        authFetchRef.current,
        workspaceId,
        taskId,
        payload,
      );
      await refresh();
      return res;
    },
    [workspaceId, refresh],
  );

  return { items, schedulerAvailable, loading, error, refresh, cancel, edit };
}
